import React, { useState, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { nanoid } from "nanoid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOut } from "@fortawesome/pro-regular-svg-icons";
import { Container } from "../grid";
import Toggler from "../toggler";
import ButtonLink from "../buttonLink";
import LogoSvg from "../../../assets/svg/logo-dark.svg";
import {
  HeaderStyle,
  HeaderBrandStyle,
  HeaderNavStyle,
  HeaderNavItemStyle,
  HeaderNavLinkStyle,
  MobileHeaderNavStyle,
} from "./header.style";
import {
  SidebarNavStyle,
  SidebarNavItemStyle,
  SidebarNavLinkStyle,
  SidebarNavDividerStyle,
} from "../sidebar/sidebar.style";

const Header = ({ pageContext, location, theme }) => {
  const { t } = useTranslation("components.global.header");
  const [mobileNavBarShown, showMobileNavBar] = useState(false);
  const { logout } = useAuth0();
  const { lng, fallbackLng } = pageContext;
  const menuItems = t("items", { returnObjects: true }).filter(item => item.show);

  const toggleSidebarMobile = () => {
    if (!mobileNavBarShown) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    showMobileNavBar(!mobileNavBarShown);
  };

  useLayoutEffect(() => {
    if (window.innerWidth < 992 && mobileNavBarShown) {
      document.body.classList.add("no-scroll");
    }

    function updateSize() {
      if (window.innerWidth < 992) {
        if (mobileNavBarShown) {
          document.body.classList.add("no-scroll");
        }
      } else {
        document.body.classList.remove("no-scroll");
      }
    }

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []); // eslint-disable-line

  const signOut = () => {
    let returnTo = process.env.GATSBY_AUTH0_LOGOUT_URI;

    if (lng !== fallbackLng) {
      returnTo += `/${lng}`;
    }

    logout({ returnTo });
  };

  return (
    <HeaderStyle>
      <Toggler
        inHeader
        ml={{ md: 5 }}
        display={{ lg: "none" }}
        onClick={toggleSidebarMobile}
      />
      <HeaderBrandStyle to="/" aria-current="page" mx="auto" display={{ lg: "none" }}>
        {typeof LogoSvg === "string"
          ? <img src={LogoSvg} alt={t("logo.alt")} width="250" height="48" />
          : <LogoSvg height="auto" width={250} viewBox="0 0 508 134" role="img" />
        }
      </HeaderBrandStyle>

      {mobileNavBarShown && (
        <MobileHeaderNavStyle display={{ lg: "none" }}>
          <SidebarNavStyle>
            {menuItems.map((item, i) => {
              const activeRoute = location.pathname?.startsWith(item.path);

              return (
                <SidebarNavItemStyle key={nanoid()}>
                  {/*External links*/}
                  {item.staticLink && item.openOnCurrentTab && (
                    <SidebarNavLinkStyle as="a" href={item.path}
                                         aria-current={activeRoute ? "page" : null}>{item.label}</SidebarNavLinkStyle>
                  )}

                  {item.staticLink && !item.openOnCurrentTab && (
                    <SidebarNavLinkStyle as="a" href={item.path} target="_blank" rel="noopener noreferrer"
                       aria-current={activeRoute ? "page" : null}>{item.label}</SidebarNavLinkStyle>
                  )}

                  {/*Internal Links*/}
                  {!item.staticLink && !item.anchorLink && (
                    <SidebarNavLinkStyle to={item.path}
                          aria-current={activeRoute ? "page" : null}>{item.label}</SidebarNavLinkStyle>
                  )}
                </SidebarNavItemStyle>
              );
            })}
            <SidebarNavDividerStyle/>
            <SidebarNavItemStyle>
              <SidebarNavLinkStyle as={ButtonLink} style={{ padding: "0.8445rem 1rem" }} onClick={signOut}>
                <FontAwesomeIcon icon={faSignOut} css={{ marginRight: 8 }} />
                {t("signOut")}
              </SidebarNavLinkStyle>
            </SidebarNavItemStyle>
          </SidebarNavStyle>
        </MobileHeaderNavStyle>
      )}

      <Container display={{ _: "none", lg: "flex" }}>
        <HeaderNavStyle mr="auto">
          {menuItems.map((item) => {
            const activeRoute = location.pathname?.startsWith(item.path);

            return (
              <HeaderNavItemStyle key={nanoid()} px={5}>
                {/*External links*/}
                {item.staticLink && item.openOnCurrentTab && (
                  <HeaderNavLinkStyle as="a" href={item.path}
                                      aria-current={activeRoute ? "page" : null}>{item.label}</HeaderNavLinkStyle>
                )}

                {item.staticLink && !item.openOnCurrentTab && (
                  <HeaderNavLinkStyle as="a" href={item.path} target="_blank" rel="noopener noreferrer"
                                      aria-current={activeRoute ? "page" : null}>{item.label}</HeaderNavLinkStyle>
                )}

                {/*Internal Links*/}
                {!item.staticLink && !item.anchorLink && (
                  <HeaderNavLinkStyle to={item.path}
                                      aria-current={activeRoute ? "page" : null}>{item.label}</HeaderNavLinkStyle>
                )}
              </HeaderNavItemStyle>
            );
          })}
        </HeaderNavStyle>

        <HeaderNavStyle px={5}>
          <ButtonLink onClick={signOut}>
            <FontAwesomeIcon icon={faSignOut} css={{ marginRight: 8 }} />
            {t("signOut")}
          </ButtonLink>
        </HeaderNavStyle>
      </Container>
    </HeaderStyle>
  );
};

Header.propTypes = {
  pageContext: PropTypes.object,
  location: PropTypes.object,
  fixed: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    path: PropTypes.string,
    link: PropTypes.bool,
    staticLink: PropTypes.bool,
    openOnCurrentTab: PropTypes.bool,
    anchorLink: PropTypes.bool,
    show: PropTypes.bool,
  })),
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
};

Header.defaultProps = {
  fixed: false,
};

export default Header;
